/**
 * Referred from https://github.com/shoelace-style/shoelace/blob/next/src/internal/slot.ts
 */

import type { ReactiveController, ReactiveElement } from 'lit';

export class SlotController implements ReactiveController {
  public static default = '[default]';

  private host: ReactiveElement;
  private slotNames: string[];

  constructor(host: ReactiveElement, ...slotNames: (string | null)[]) {
    this.host = host;

    // Normalize null values to track the default slot
    this.slotNames = slotNames.length ? slotNames.map((slot) => slot ?? SlotController.default) : [SlotController.default];

    this.host.addController(this);
  }

  private hasDefaultSlot(): boolean {
    return Array.from(this.host.childNodes).some((node) => {
      if (node.nodeType === Node.TEXT_NODE && node.textContent!.trim() !== '') {
        return true;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;

        // Elements without a slot attribute belong to the default slot
        if (!el.hasAttribute('slot')) {
          return true;
        }
      }

      return false;
    });
  }

  private hasNamedSlot(name: string): boolean {
    return this.host.querySelector(`:scope > [slot="${name}"]`) !== null;
  }

  /**
   * Checks if a given slot has content.
   */
  hasSlotted(slotName: string | null = SlotController.default): boolean {
    const normalizedSlot = slotName ?? SlotController.default;
    return normalizedSlot === SlotController.default ? this.hasDefaultSlot() : this.hasNamedSlot(normalizedSlot);
  }

  /**
   * Checks if all requested slots are empty.
   */
  isEmpty(...names: (string | null | undefined)[]): boolean {
    return !this.hasSlotted(...names);
  }

  /**
   * Retrieves the elements assigned to a given slot.
   */
  getSlotted<T extends Element = Element>(slotName: string | null = SlotController.default): T[] {
    const normalizedSlot = slotName ?? SlotController.default;
    return normalizedSlot === SlotController.default
      ? (Array.from(this.host.children).filter((el) => !el.hasAttribute('slot')) as T[])
      : Array.from(this.host.querySelectorAll(`:scope > [slot="${normalizedSlot}"]`));
  }

  hostConnected() {
    this.host.shadowRoot?.addEventListener('slotchange', this.handleSlotChange);
  }

  hostDisconnected() {
    this.host.shadowRoot?.removeEventListener('slotchange', this.handleSlotChange);
  }

  private handleSlotChange = (event: Event) => {
    const slot = event.target as HTMLSlotElement;
    const normalizedSlotName = slot.name || SlotController.default;

    if (this.slotNames.includes(normalizedSlotName)) {
      this.host.requestUpdate();
    }
  };
}
